import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRightIcon, XMarkIcon, CalendarDaysIcon, CurrencyRupeeIcon, TrophyIcon } from '@heroicons/react/24/outline';

const CompetitionCard = ({ data, onJoin, processingId, disabled }) => {
  const [showDescModal, setShowDescModal] = useState(false);
  const isProcessing = processingId === data.id;

  // ── Expired check ────────────────────────────────────────────────────────────
  const isExpired = disabled ||
    (data.end_date && new Date(data.end_date) < new Date());

  // ── Formatters ───────────────────────────────────────────────────────────────
  const renderIncentive = (val) => {
    if (!val || val === "0" || val === 0) return "Recognition";
    const numericVal = Number(val);
    if (!isNaN(numericVal)) return `₹${numericVal.toLocaleString('en-IN')}`;
    return String(val);
  };

  const formatCurrency = (val) => {
    if (!val || val === "0" || val === 0) return "Free";
    const numericVal = Number(val);
    return isNaN(numericVal) ? val : `₹${numericVal.toLocaleString('en-IN')}`;
  };

  const formatDateRange = (start, end) => {
    if (!start || !end) return "Dates TBA";
    const s = new Date(start);
    const e = new Date(end);
    const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
    const sameYear = s.getFullYear() === e.getFullYear();
    if (sameMonth) {
      return `${s.getDate()}–${e.getDate()} ${s.toLocaleDateString("en-GB", { month: "short" })}`;
    }
    if (sameYear) {
      return `${s.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – ${e.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`;
    }
    return `${s.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })} – ${e.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`;
  };

  const description = data.description || "Structured industry evaluation challenge.";
  const isLongDesc = description.length > 100;
  // Short preview — first 100 chars
  const previewDesc = isLongDesc ? description.slice(0, 100).trimEnd() + '...' : description;

  return (
    <>
      {/* ── CARD ─────────────────────────────────────────────────────────────── */}
      <div
        className={`flex flex-col bg-[#0b0b0b] border border-white/10 rounded-2xl overflow-hidden transition-colors duration-300 shadow-lg ${
          isExpired ? 'opacity-70' : 'hover:border-blue-500/40'
        }`}
      >
        {/* Header */}
        <div className="px-6 pt-6 pb-4 flex justify-between items-start">
          <span
            className={`text-xs px-3 py-1 rounded-full border font-medium capitalize ${
              isExpired
                ? 'text-gray-500 border-gray-800 bg-gray-900'
                : 'text-blue-400 border-blue-500/30 bg-blue-500/10'
            }`}
          >
            {data.normalizedType || 'Competition'}
          </span>
          <span className="text-xs text-gray-500 font-mono">
            {formatDateRange(data.start_date, data.end_date)}
          </span>
        </div>

        {/* Body */}
        <div className="px-6 pb-6 flex-grow">
          <h3 className={`text-lg font-semibold mb-3 leading-snug ${isExpired ? 'text-gray-500' : 'text-white'}`}>
            {data.title}
          </h3>

          {/* ✅ Always show fixed 2-line preview — never expands inline */}
          <p className="text-sm text-gray-500 leading-relaxed line-clamp-2">
            {previewDesc}
          </p>

          {/* Read more opens modal — keeps card height consistent */}
          {isLongDesc && (
            <button
              onClick={() => setShowDescModal(true)}
              className="mt-2 text-xs text-blue-400 hover:text-blue-300 transition-colors duration-200 underline underline-offset-2"
            >
              Read more
            </button>
          )}
        </div>

        {/* Info Grid */}
        <div className="px-6 py-4 border-t border-white/5 bg-white/[0.02] grid grid-cols-2 gap-6 text-sm">
          <div>
            <div className="text-gray-500 mb-1 text-xs uppercase tracking-wide">Entry Fee</div>
            <div className="text-gray-300 font-medium">
              {formatCurrency(data.entry_fee || data.fee)}
            </div>
          </div>
          <div>
            <div className="text-gray-500 mb-1 text-xs uppercase tracking-wide">Incentive</div>
            <div className={`font-medium text-sm leading-snug ${isExpired ? 'text-gray-600' : 'text-emerald-400'}`}>
              {renderIncentive(data.prize_pool || data.reward)}
            </div>
          </div>
        </div>

        {/* Action */}
        <button
          disabled={isProcessing || isExpired}
          onClick={() => onJoin(data)}
          className={`w-full py-4 text-sm font-medium transition-colors duration-300 flex items-center justify-center gap-2 ${
            isExpired
              ? 'bg-gray-900 text-gray-600 cursor-not-allowed'
              : isProcessing
              ? 'bg-blue-700 text-white cursor-wait'
              : 'bg-white text-black hover:bg-blue-600 hover:text-white'
          }`}
        >
          {isProcessing ? (
            <>
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Processing...
            </>
          ) : isExpired ? (
            'Registration Closed'
          ) : (
            <>Join <ChevronRightIcon className="h-4 w-4" /></>
          )}
        </button>
      </div>

      {/* ── DESCRIPTION MODAL (portal — renders outside card) ────────────────── */}
      {showDescModal && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setShowDescModal(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

          {/* Modal panel */}
          <div
            className="relative z-10 w-full sm:max-w-lg bg-[#0f0f0f] border border-white/10 rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()} // prevent backdrop close when clicking inside
          >
            {/* Modal header */}
            <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-white/5">
              <div className="flex-1 pr-4">
                {/* Type badge */}
                <span className="text-[10px] px-2 py-0.5 rounded-full border border-blue-500/30 bg-blue-500/10 text-blue-400 font-medium capitalize mb-2 inline-block">
                  {data.normalizedType || 'Competition'}
                </span>
                <h2 className="text-base font-bold text-white leading-snug mt-1">
                  {data.title}
                </h2>
              </div>
              {/* Close button */}
              <button
                onClick={() => setShowDescModal(false)}
                className="shrink-0 p-2 rounded-xl bg-white/5 hover:bg-white/10 text-gray-400 hover:text-white transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Modal body — scrollable */}
            <div className="px-6 py-5 max-h-[50vh] overflow-y-auto">
              <p className="text-sm text-gray-400 leading-relaxed whitespace-pre-line">
                {description}
              </p>
            </div>

            {/* Modal footer — key details */}
            <div className="px-6 py-4 border-t border-white/5 bg-white/[0.02] grid grid-cols-3 gap-4">
              {/* Date */}
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-1 text-gray-500">
                  <CalendarDaysIcon className="h-3 w-3" />
                  <span className="text-[10px] uppercase tracking-wide">Dates</span>
                </div>
                <span className="text-xs text-gray-300 font-medium">
                  {formatDateRange(data.start_date, data.end_date)}
                </span>
              </div>

              {/* Fee */}
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-1 text-gray-500">
                  <CurrencyRupeeIcon className="h-3 w-3" />
                  <span className="text-[10px] uppercase tracking-wide">Fee</span>
                </div>
                <span className="text-xs text-gray-300 font-medium">
                  {formatCurrency(data.entry_fee || data.fee)}
                </span>
              </div>

              {/* Incentive */}
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-1 text-gray-500">
                  <TrophyIcon className="h-3 w-3" />
                  <span className="text-[10px] uppercase tracking-wide">Prize</span>
                </div>
                <span className="text-xs text-emerald-400 font-medium leading-tight">
                  {renderIncentive(data.prize_pool || data.reward)}
                </span>
              </div>
            </div>

            {/* Modal CTA */}
            <div className="px-6 pb-6 pt-4">
              <button
                disabled={isProcessing || isExpired}
                onClick={() => {
                  setShowDescModal(false);
                  onJoin(data);
                }}
                className={`w-full py-3.5 rounded-xl text-sm font-bold transition-colors flex items-center justify-center gap-2 ${
                  isExpired
                    ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                    : isProcessing
                    ? 'bg-blue-700 text-white cursor-wait'
                    : 'bg-white text-black hover:bg-blue-600 hover:text-white'
                }`}
              >
                {isProcessing ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Processing...
                  </>
                ) : isExpired ? (
                  'Registration Closed'
                ) : (
                  <>Join Now <ChevronRightIcon className="h-4 w-4" /></>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default CompetitionCard;